import axios from 'axios';
import BaseModel from 'src/model/base.model';
import tokenService from 'src/services/token/TokenService';
import env from 'src/shared/env';

class BaseAPIService<T extends BaseModel> {
  public baseURL: string = env.REACT_APP_BASE_API_URL;

  constructor(public base: typeof BaseModel) {}

  public async doRequest(opts: any): Promise<any> {
    const options = await tokenService
      .getAccessToken()
      .then(token => {
        let headers = {
          ...opts.headers
        };
        if (token !== null) {
          // Only add token if it is valid
          headers = {
            Authorization: `Bearer ${token}`,
            ...headers
          };
        }
        return {
          ...opts,
          headers
        };
      })
      .catch(error => {
        throw error;
      });

    return axios(options).catch(error => {
      if (error.response) {
        // The request was made and the server responded with a status code that falls
        // out of the range of 2xx
        if (error.response.status === 401) {
          return tokenService
            .refreshToken()
            .then(response => {
              return this.doRequest(opts);
            })
            .catch(e => {
              tokenService.logOut();
              window.location.reload();
            });
        }

        throw error;
      } else if (error.request) {
        // The request was made but no response was received `error.request` is an
        // instance of XMLHttpRequest in the browser and an instance of
        // http.ClientRequest in node.js
        throw error;
      } else {
        // Something happened in setting up the request that triggered an Error
        throw new Error('Error' + error.message);
      }
    });
  }

  public getOneByParams(
    url: string,
    params: any = {},
    headers: any = {}
  ): Promise<T> {
    return this.doRequest({
      baseURL: this.baseURL,
      method: 'get',
      params,
      headers,
      url
    }).then(res => {
      return this.base.cast<T>(res.data, this.base);
    });
  }

  public getBrandsOneByParams(
    url: string,
    params: any = {},
    headers: any = {}
  ): Promise<T> {
    return this.doRequest({
      baseURL: this.baseURL,
      method: 'get',
      params,
      headers,
      url
    }).then(res => {
      return this.base.cast<T>(res, this.base);
    });
  }

  public getOneByParamsPatch(
    url: string,
    params: any = {},
    headers: any = {}
  ): Promise<T> {
    return this.doRequest({
      baseURL: this.baseURL,
      method: 'patch',
      params,
      headers,
      url
    }).then(res => {
      return this.base.cast<T>(res.data, this.base);
    });
  }

  public getOneById(url: string, id: number, headers: any = {}): Promise<T> {
    return this.doRequest({
      baseURL: this.baseURL,
      method: 'get',
      headers,
      url: url + id
    }).then(res => {
      return this.base.cast<T>(res.data, this.base);
    });
  }

  public getByParams(
    url: string,
    params: any = {},
    headers: any = {}
  ): Promise<T[]> {
    return this.doRequest({
      baseURL: this.baseURL,
      method: 'get',
      params,
      headers,
      url
    }).then(res => {
      return this.base.castArray<T>(res.data, this.base);
    });
  }

  public getAll(url: string, headers: any = {}): Promise<T[]> {
    return this.doRequest({
      baseURL: this.baseURL,
      method: 'get',
      headers,
      url
    }).then(res => {
      return this.base.castArray<T>(res.data, this.base);
    });
  }

  public create(
    url: string,
    params: any = {},
    headers: any = {}
  ): Promise<any> {
    return this.doRequest({
      baseURL: this.baseURL,
      data: params,
      method: 'post',
      headers,
      url
    }).then(res => {
      return this.base.cast<T>(res.data, this.base);
    });
  }

  public update(
    url: string,
    id: number,
    params: any = {},
    headers: any = {}
  ): Promise<T> {
    return this.doRequest({
      baseURL: this.baseURL,
      data: params,
      method: 'put',
      headers,
      url: url + id
    }).then(res => {
      return this.base.cast<T>(res.data, this.base);
    });
  }

  public delete(url: string, id: number, headers: any = {}): Promise<any> {
    return this.doRequest({
      baseURL: this.baseURL,
      method: 'delete',
      headers,
      url: url + id
    }).then(res => {
      return res;
    });
  }
}

export default BaseAPIService;
